
import React, { useState, useEffect, useMemo } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    Paper,
    Autocomplete,
    CircularProgress,
    Tooltip,
    Divider,
    Chip
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ListIcon from "@mui/icons-material/List";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {useTheme} from '@mui/material/styles';
import {ListViewModel} from '../../models/models';
import {GlobalSearchResultModel} from '../../models/modelsGlobalSearch';
import { useSnackbar } from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams, useNavigate} from 'react-router-dom';
import { getAppName, getObjectName } from "../../utils/helperRouter";
import { globalSearchRecords } from "../../services/backendApi";
import { useAuth } from "../../hooks/useAuth";
import PeopleIcon from '@mui/icons-material/People';
import debounce from "lodash.debounce";
import {SearchFieldComponent} from "../../components/searchField/SearchField";



export const GlobalSearchFieldComponent: React.FC<{
}> = ({
}) => {

  const { enqueueSnackbar } = useSnackbar();

  const [searchText, setSearchText] = useState<string>("");

  const [searchRecords, setSearchRecords] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<GlobalSearchResultModel[]>([]);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>("");

  const {accessToken, onInvalidToken} = useAuth();
  let {appName, objectName, recordUuid} = useParams();

  const navigate = useNavigate();


  const searchRecordsDebounced = useMemo(
    () =>
    debounce(async (search: string) => {
      if(!search){
        setSearchRecords([]);
        setIsLoading(false);
        return;
      }

      const thisSearchText = search.trim()

      globalSearchRecords(accessToken, thisSearchText)
         .then((results) => {

           setSearchResults(results)

           const newSearchRecords = results.map((thisResult: GlobalSearchResultModel) => {
             return thisResult.records.slice(0,3)
           }).flat()
             .slice(0,50)

           const viewAllOption = {
             label: "View All",
             isViewAll: true
           }
           setSearchRecords([viewAllOption, ...newSearchRecords]);
           setIsLoading(false);
           setErrorMessage("");
         })
         .catch((err) => {
           setErrorMessage(err.message)
           setIsLoading(false)
         })
    }, 1000),
    []
  );


  useEffect(() => {
    if(searchText.trim() !== "" &&
       searchText.trim().length >= 4){
      setIsLoading(true)
      searchRecordsDebounced(searchText)
    } else {
      setSearchRecords([])
    }

  }, [searchText, searchRecordsDebounced])


  const handleClickEvent = () => {
    console.log('click')
  }

  const handleLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    //need to stop clicking "button" which is the complete row
    //when clicking a link
    handleClear();
    event.stopPropagation();
  }


  const handleClear = () => {
    setSearchText("");
  }

  const handleSearch = (searchText: string) => {
        enqueueSnackbar("Search not yet properly implemented", {
          variant: "warning",
      });
    return
  }


  const getSearchRecordLink = (searchRecord: any) => {
    const appApiName = getAppName(appName);
    const link_to = '/' + appApiName + '/' + searchRecord.objectMetadata.ApiName_sys + '/' + searchRecord.Uuid_sys;
    return link_to
  }


  const getSearchRecordLabel = (searchRecord: any) => {
    //find field that is uses for labelling
    const ignoreFields = ['Id_sys', 'Uuid_sys']
//    console.log(searchRecord)
//    console.log(labelValue)
    return (
      <Box key={`${searchRecord.objectMetadata.Uuid_sys}-${searchRecord.Uuid_sys}`}>
        <Typography style={{fontWeight: 'bold'}}>
          {searchRecord.objectMetadata.Label_sys}: {searchRecord.LookupDisplayValue_sys}
        </Typography>
        {searchRecord.fields.map((thisField: any) => {
          if(!ignoreFields.includes(thisField.fieldName)){
            return (
              <Box>
                 {thisField.fieldLabel}: {thisField.fieldValue}
              </Box>
            )
          }
        })
      }
      </Box>
    )
  }


  return (
   <Box>

    <Box sx={{
          display: "flex",
          gap: "1rem",
          alignItems: 'center',
          pt: '1rem',
          pl: '1rem',
          pr: '1rem',
        }}
    >

     <Box  sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "0.5rem",
          alignItems: 'center'
        }}
        >


    <Box sx={{
      display: "flex",
      flexDirection: "row",
      minWidth: "20rem"
    }}
    >

        <Autocomplete
          freeSolo
          disableClearable
          options={searchRecords}
          fullWidth
          getOptionLabel={(option) => {
            if(option.isViewAll) return option.label
            return option.LookupDisplayValue_sys
          }}
          filterOptions={(x) => x}
          onInputChange={(_, value, reason) => {
            if(reason !== 'reset') {
              setSearchText(value)
            }
          }}
          onChange={(_, value) => {
            //do nothing because handled via Link anyways
          }}
          inputValue={searchText}
          renderOption={(props, option) => {
            const appApiName = getAppName(appName);

            if(option.isViewAll){
              return (
                <li
                  {...props}
                  key={`${option.label}`}
                >
                  <Link
                    to={`/${appApiName}/customPage/search?q=${encodeURIComponent(searchText)}`} onClick={handleLinkClick}
                  >
                    {option.label}
                  </Link>
                 </li>
                )
            }
            return (
              <li
                {...props}
                key={`${option.objectMetadata.Uuid_sys}-${option.Uuid_sys}`}
              >
                <Link to={getSearchRecordLink(option)} onClick={handleLinkClick}>
                  {getSearchRecordLabel(option)}
                </Link>
              </li>
            )
           }
          }
          renderInput={(params) => (
            <TextField
              {...params}
              error={errorMessage !== ""}
              required={false}
              size="small"
              label="Search"
              variant="filled"
              helperText={errorMessage}
              placeholder="Type email or name"
              onKeyDown={(e) => {
                if(e.key === 'Enter'){
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      {isLoading && <CircularProgress size={18}/>}
                      {
                        searchText ? (
                          <InputAdornment position="end">
                            <Tooltip title="Clear">
                              <IconButton disabled={searchText === ""} size="small" onClick={() => handleClear()}>
                                <ClearIcon />
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ) : (
                          <InputAdornment position="end">
                            <SearchIcon />
                          </InputAdornment>
                        )
                      }

                      {params.InputProps.endAdornment}
                  </Box>
                )
              }}
            />
          )
          }
        />
        </Box>
    </Box>

      </Box>
      </Box>

    )
};
